"use client";

import { fetchSearchResultswithCache } from "@/actions/enterpriseSearchAction";
import { SkeletonSquare, SkeletonText } from "@/components/ui/Skeleton";
import { generateLocalizedURL } from "@/utils/url";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Suspense, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState, useTransition } from "react";

const iitvDic = {
  english: {
    search: {
      placeholder: "Search Iran International",
      sort: "Sort by",
      best: "Best",
      newest: "Newest",
      oldest: "Oldest",
      summary: "Showing {total} results for {query}:",
      page: "Page {page}",
      not: "No results found for {query}.",
      loadMore: "Next Page",
      loading: "Loading...",
    },
  },
  farsi: {
    search: {
      placeholder: "جستجو در ایران اینترنشنال",
      sort: "مرتب‌سازی بر اساس",
      best: "بهترین",
      newest: "جدیدترین",
      oldest: "قدیمی‌ترین",
      summary: "نمایش {total} نتیجه برای {query}:",
      page: "صفحه {page}",
      not: "نتیجه‌ای برای {query} یافت نشد.",
      loadMore: "صفحه بعد",
      loading: "در حال بارگذاری...",
    },
  },
  arabic: {
    search: {
      placeholder: "ابحث في إيران إنترناشيونال",
      sort: "ترتيب حسب",
      best: "الأفضل",
      newest: "الأحدث",
      oldest: "الأقدم",
      summary: "عرض {total} نتيجة لـ {query}:",
      page: "الصفحة {page}",
      not: "لم يتم العثور على نتائج لـ {query}.",
      loadMore: "الصفحة التالية",
      loading: "جار التحميل...",
    },
  },
  pashto: {
    search: {
      placeholder: "په افغانستان انټرنشنل پښتو کې لټون",
      sort: "ترتیب بر اساس",
      best: "غوره",
      newest: "نوی",
      oldest: "زوړ",
      summary: "{total} پایلې د {query} لپاره:",
      page: "پاڼه {page}",
      not: "د {query} لپاره هیڅ نتیجه ونه موندل شوه.",
      loadMore: "بله پاڼه",
      loading: "په بارولو کې...",
    },
  }  
};

const aitvDic = {
  english: {
    search: {
      placeholder: "Search Afganistan International",
      sort: "Sort by",
      best: "Best",
      newest: "Newest",
      oldest: "Oldest",
      summary: "Showing {total} results for {query}:",
      page: "Page {page}",
      not: "No results found for {query}.",
      loadMore: "Next Page",
      loading: "Loading...",
    },
  },
  farsi: {
    search: {
      placeholder: "جستجو در افغانستان اینترنشنال",
      sort: "مرتب‌سازی بر اساس",
      best: "بهترین",
      newest: "جدیدترین",
      oldest: "قدیمی‌ترین",
      summary: "نمایش {total} نتیجه برای {query}:",
      page: "صفحه {page}",
      not: "نتیجه‌ای برای {query} یافت نشد.",
      loadMore: "صفحه بعد",
      loading: "در حال بارگذاری...",
    },
  },
  pashto: {
    search: {
      placeholder: "په افغانستان انټرنشنل پښتو کې لټون",
      sort: "ترتیب بر اساس",
      best: "غوره",
      newest: "نوی",
      oldest: "زوړ",
      summary: "{total} پایلې د {query} لپاره:",
      page: "پاڼه {page}",
      not: "د {query} لپاره هیڅ نتیجه ونه موندل شوه.",
      loadMore: "بله پاڼه",
      loading: "په بارولو کې...",
    },
  },
  arabic: {
    search: {
      placeholder: "ابحث في أفغانستان الدولية",
      sort: "ترتيب حسب",
      best: "الأفضل",
      newest: "الأحدث",
      oldest: "الأقدم",
      summary: "عرض {total} نتيجة لـ {query}:",
      page: "الصفحة {page}",
      not: "لم يتم العثور على نتائج لـ {query}.",
      loadMore: "الصفحة التالية",
      loading: "جار التحميل...",
    },
  }
};

const dict = process.env.OUTLET === "iitv" ? iitvDic : aitvDic;

type SortOption = "best" | "newest" | "oldest";

const SORT_OPTIONS: Record<SortOption, string | { field: string; direction: "asc" | "desc" }> = {
  best: "best",
  newest: { field: "published_at", direction: "desc" },
  oldest: { field: "published_at", direction: "asc" },
};

export default function SearchWrapper({
  initialQuery,
  initialSort,
  initialPage,
  language,
}: {
  initialQuery: string;
  initialSort: string;
  initialPage: string;
  language: "farsi" | "english" | "pashto" | "arabic";
}) {

  const router = useRouter();
  const dictionary = useMemo(() => dict[language].search, [language]);

  const [query, setQuery] = useState<string>(initialQuery || "");
  const deferredQuery = useDeferredValue<string>(query);

  const inputRef = useRef<HTMLInputElement>(null);

  const [sort, setSort] = useState<SortOption>(initialSort as SortOption || "newest");

  const [page, setPage] = useState<number>(parseInt(initialPage) || 1);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return <>
    <section className="search">
      <header className="search__container">
        <div className="search__input">
          <input
            id="query"
            ref={inputRef}
            type="text"
            tabIndex={1}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={dictionary.placeholder}
            autoComplete="off"
          />
        </div>
        <div className="search__sort">
          <label htmlFor="sort">{dictionary.sort}</label>
          <select
            id="sort"
            name="sort"
            tabIndex={2}
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOption)}
            className="search__select"
          >
            <option value="best">{dictionary.best}</option>
            <option value="newest">{dictionary.newest}</option>
            <option value="oldest">{dictionary.oldest}</option>
          </select>
        </div>
      </header>
    </section>
    <section className="search__results">
      <Suspense fallback={<></>}>
        <SearchResults
          language={language}
          query={deferredQuery.trim()}
          sort={sort}
          page={page}
        />
      </Suspense>
    </section>
  </>
}

export function SearchResults({
  language,
  query,
  sort,
  page,
}: {
  language: "farsi" | "english" | "pashto" | "arabic";
  query: string;
  sort: SortOption;
  page: number;
}) {

  const router = useRouter();

  const numberFormatter = useMemo(() => new Intl.NumberFormat(language === "farsi" ? "fa-IR" : "en-US"), [language]);

  const sortOption = useMemo(() => SORT_OPTIONS[sort], [sort]) as "best" | { field: string; direction: "asc" | "desc" };

  const [currentPage, setCurrentPage] = useState<number>(page);

  const [results, setResults] = useState<any[]>([]);
  const [searchMetadata, setSearchMetadata] = useState<{ total: number; totalPages: number; }>({ total: 0, totalPages: 0 });

  const [isPending, startTransition] = useTransition();

  const fetchResults = useCallback(async (page: number = 1) => {
    if (!query || query.trim() === "" || query.length < 3) {
      setResults([]);
      setSearchMetadata({ total: 0, totalPages: 0 });
      return;
    }
    startTransition(async () => {
      try {
        const response = await fetchSearchResultswithCache(
          query,
          language,
          {
            page: page,
            size: 20,
            order: sortOption,
          }
        );
        setResults(response.results);
        setSearchMetadata({
          total: response.total,
          totalPages: response.totalPages as number,
        });
      } catch (error) {
        setResults([]);
        setSearchMetadata({ total: 0, totalPages: 0 });
        console.error(error);
      }
    });
  }, [query, sort]);

  useEffect(() => {
    fetchResults(currentPage);
    router.push(`?query=${query}&sort=${sort}&page=${currentPage}`);
  }, [query, sort, currentPage]);

  function handleLoadMore() {
    if (currentPage < searchMetadata.totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
    }
  }

  return (
    <div className="search__results">
      {searchMetadata.total > 0 && <div className="results__summary">
        <FormatWithHTML template={dict[language].search.summary} values={{ total: numberFormatter.format(searchMetadata.total), query }} />
        {page > 1 && <div className="results__page">
        <FormatWithHTML template={dict[language].search.page} values={{ page: numberFormatter.format(currentPage) }} />
      </div>}
      </div>}
      {isPending
        ?
        <ul className="results__list">
          {skeletonResults()}
        </ul>
        :
        <>
          <ul className="results__list">
            {results.map((result) => (
              result.slug && <li key={result.id} className="results__item">
                {renderEditorialItem(result)}
              </li>
            ))}
          </ul>
        </>
      }
      {!isPending && query && results.length === 0 && <div className="results__empty">
        <FormatWithHTML template={dict[language].search.not} values={{ query: query }} />
      </div>}
      {searchMetadata.totalPages > 1 && <div className="results__pagination">
        <button
          className="results__pagination-button"
          onClick={() => handleLoadMore()}
          disabled={page >= searchMetadata.totalPages}
        >
          {isPending ? dict[language].search.loading : dict[language].search.loadMore}
        </button>
      </div>}
    </div>
  );
}

function skeletonResults() {
  return Array.from({ length: 5 }).map((_, index) => (
    <li key={index} className="results__item">
      <article className="item">
        <main className="item__content">
          <h2 className="item__headline">
            <SkeletonText type="large" lines={1} />
          </h2>
          <div className="item__lead">
            <SkeletonText type="normal" lines={3} />
          </div>
        </main>
        <figure className="item__figure">
          <SkeletonSquare />
        </figure>
      </article>
    </li>
  ));
}

function renderEditorialItem(item: any) {
  if (!item || !item.slug) return null;
  const href = generateLocalizedURL(item.type === "ottvideo" ? `video/${item.slug}` : item.slug, item.language)
  return (
    <article className="item">
      <main className="item__content">
        <h2 className="item__headline">
          <Link
            className="item__link"
            href={href}
            target="_blank"
            prefetch={false}
          >
            {item.headline}
          </Link>
        </h2>
        <p className="item__lead">{item.lead}</p>
        <time
          dateTime={item.published_at}
          className="item__time"
        >
          {formatRelativeTime(new Date(item.published_at), item.language === "farsi" ? "fa-IR" : "en-US")}
        </time>
      </main>
      <figure className="item__figure">
        <Image
          src={item.image ? item.image : "/images/placeholder-16x9.svg"}
          alt={item.headline}
          width={320}
          height={180}
        />
      </figure>
    </article>
  );
}

const formatRelativeTime = (date: Date, locale: string) => {
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  const diffInMinutes = Math.floor(diffInSeconds / 60)
  const diffInHours = Math.floor(diffInMinutes / 60)

  const relativeTimeFormatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

  if (diffInMinutes < 60) {
    return relativeTimeFormatter.format(-diffInMinutes, 'minute')
  } else if (diffInHours < 24) {
    return relativeTimeFormatter.format(-diffInHours, 'hour')
  } else {
    return date.toLocaleDateString(locale, {
      weekday: 'long',
      month: 'numeric',
      year: 'numeric',
      day: 'numeric',
    })
  }
}

const FormatWithHTML = ({ template, values }: { template: string; values: Record<string, string | number>; }) => {
  const renderFormattedText = () => {
    return template.split(/(\{.*?\})/g).map((part, index) => {
      if (part.startsWith("{") && part.endsWith("}")) {
        const key = part.slice(1, -1);
        const value = values[key];
        if (value === undefined) {
          throw new Error(`Missing value for key: ${key}`);
        }
        return <strong key={index}>{value}</strong>;
      }
      return <span key={index}>{part}</span>;
    });
  };

  return <>{renderFormattedText()}</>;
};